import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Badge from '../components/UI/Badge';
import Table from '../components/UI/Table';
import { strategiesAPI, tradesAPI, positionsAPI } from '../services/api';
import { PageTransition, Spinner } from '@/components/motion';
import formatMoney from '@/lib/formatMoney';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine,
} from 'recharts';

/* ── Helpers ── */
const pnlClass = (v) => (v >= 0 ? 'text-green-400' : 'text-red-400');

/* ── Equity Chart (cumulative P&L from trade history) ── */
// Resolve a row's mode from its own column. We read trade.trading_mode /
// position.trading_mode directly rather than deriving via strategy lookup —
// the strategy-map approach silently buckets archived-strategy rows as
// "paper" and retroactively reclassifies history if a strategy's mode is
// flipped. The row's own column is the immutable truth captured at fill
// time (or for positions, JOINed in by Position.findAll).
const rowMode = (row) => row?.trading_mode === 'live' ? 'live' : 'paper';

function EquityMiniChart({ trades, mode }) {
  const data = useMemo(() => {
    if (!trades?.length) return [];

    // Filter by mode if specified
    const filtered = mode ? trades.filter(t => rowMode(t) === mode) : trades;
    if (!filtered.length) return [];

    // Sort by date ascending
    const sorted = [...filtered].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    // Build cumulative P&L series
    let cumPnL = 0;
    const points = [];
    for (const trade of sorted) {
      if (trade.side === 'sell') {
        cumPnL += parseFloat(trade.pnl || 0);
      }
      points.push({
        date: new Date(trade.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        pnl: Math.round(cumPnL * 100) / 100,
      });
    }

    // Deduplicate by date (keep last point per day)
    const byDate = new Map();
    for (const pt of points) byDate.set(pt.date, pt);
    return [...byDate.values()];
  }, [trades, mode]);

  if (data.length < 2) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground/30 text-xs">
        Not enough data
      </div>
    );
  }

  const final = data[data.length - 1]?.pnl || 0;
  const color = final >= 0 ? '#22c55e' : '#ef4444';

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
        <defs>
          <linearGradient id={`eqGrad-${mode || 'all'}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.25} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="date" hide />
        <YAxis domain={['auto', 'auto']} hide />
        <Tooltip
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const pt = payload[0]?.payload;
            return (
              <div className="bg-[#18181b] border border-[#374151] rounded-lg px-2.5 py-1.5 shadow-xl text-[11px]">
                <p className="text-[#9ca3af] text-[10px]">{pt.date}</p>
                <p className={pt.pnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                  {formatMoney(pt.pnl, { sign: true })}
                </p>
              </div>
            );
          }}
        />
        <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="3 3" strokeWidth={0.5} />
        <Area
          type="monotone"
          dataKey="pnl"
          stroke={color}
          strokeWidth={1.5}
          fill={`url(#eqGrad-${mode || 'all'})`}
          dot={false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [strategies, setStrategies] = useState([]);
  const [recentTrades, setRecentTrades] = useState([]);
  const [allTrades, setAllTrades] = useState([]);
  const [positions, setPositions] = useState([]);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      const [strategiesData, tradesData, allTradesData, positionsData] = await Promise.all([
        strategiesAPI.getAll().catch(() => ({ data: [] })),
        tradesAPI.getAll({ limit: 10 }).catch(() => ({ data: [] })),
        tradesAPI.getAll({ limit: 500 }).catch(() => ({ data: [] })),
        positionsAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setStrategies(strategiesData.data || []);
      setRecentTrades(tradesData.data || []);
      setAllTrades(allTradesData.data || []);
      setPositions(positionsData.data || []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const strategyMap = useMemo(() => {
    const map = new Map();
    for (const s of strategies) map.set(s.id, s);
    return map;
  }, [strategies]);

  const stats = useMemo(() => {
    const openPos = positions.filter((p) => p.status === 'open');
    const allPos = positions;

    const calcPnL = (posArray) => {
      const unrealized = posArray.filter(p => p.status === 'open').reduce((s, p) => s + parseFloat(p.unrealized_pnl || 0), 0);
      const realized = posArray.reduce((s, p) => s + parseFloat(p.realized_pnl || 0), 0);
      return { unrealized, realized, total: unrealized + realized };
    };

    const livePos = allPos.filter((p) => rowMode(p) === 'live');
    const paperPos = allPos.filter((p) => rowMode(p) !== 'live');

    const livePnL = calcPnL(livePos);
    const paperPnL = calcPnL(paperPos);
    const totalPnL = calcPnL(allPos);

    const nonArchived = strategies.filter(s => s.status !== 'archived');
    const liveStrategies = nonArchived.filter(s => s.trading_mode === 'live');
    const paperStrategies = nonArchived.filter(s => s.trading_mode !== 'live');

    const liveOpenPos = openPos.filter(p => rowMode(p) === 'live');
    const paperOpenPos = openPos.filter(p => rowMode(p) !== 'live');
    const liveExposure = liveOpenPos.reduce(
      (sum, pos) => sum + parseFloat(pos.quantity) * parseFloat(pos.current_price || pos.entry_price), 0
    );
    const paperExposure = paperOpenPos.reduce(
      (sum, pos) => sum + parseFloat(pos.quantity) * parseFloat(pos.current_price || pos.entry_price), 0
    );

    return {
      totalStrategies: nonArchived.length,
      liveStrategies: liveStrategies.length,
      paperStrategies: paperStrategies.length,
      activeStrategies: strategies.filter(s => s.status === 'running').length,
      liveActive: liveStrategies.filter(s => s.status === 'running').length,
      paperActive: paperStrategies.filter(s => s.status === 'running').length,
      totalPnL, livePnL, paperPnL,
      openPositions: openPos.length,
      liveOpenPositions: liveOpenPos.length,
      paperOpenPositions: paperOpenPos.length,
      liveExposure, paperExposure,
    };
  }, [positions, strategies]);

  const tradeCounts = useMemo(() => {
    let live = 0;
    let paper = 0;
    for (const t of allTrades) {
      if (rowMode(t) === 'live') live++; else paper++;
    }
    return { live, paper };
  }, [allTrades]);

  // ── Table columns ──

  const strategyColumns = [
    {
      key: 'name', label: 'Strategy',
      render: (name, row) => (
        <div>
          <p className="font-semibold text-foreground">{name}</p>
          <p className="text-xs text-muted-foreground/70 capitalize">{row.type?.replace('_', ' ')}</p>
        </div>
      ),
    },
    {
      key: 'status', label: 'Status',
      render: (status) => {
        const variant = { running: 'success', stopped: 'neutral', paused: 'warning', error: 'danger' }[status] || 'neutral';
        return <Badge variant={variant}>{status.toUpperCase()}</Badge>;
      },
    },
    {
      key: 'trading_mode', label: 'Mode',
      render: (mode) => (
        <Badge variant={mode === 'paper' ? 'info' : 'warning'}>
          {mode === 'paper' ? 'PAPER' : 'LIVE'}
        </Badge>
      ),
    },
    {
      key: 'actions', label: '',
      render: (_, row) => (
        <Button size="sm" variant="ghost" onClick={() => navigate(`/strategies/trading-floor/${row.id}`)}>
          Monitor
        </Button>
      ),
    },
  ];

  const tradeColumns = [
    { key: 'symbol', label: 'Symbol', render: (symbol) => <span className="font-semibold text-foreground">{symbol}</span> },
    { key: 'side', label: 'Side', render: (side) => <Badge variant={side === 'buy' ? 'success' : 'danger'}>{side.toUpperCase()}</Badge> },
    {
      key: 'strategy_id', label: 'Strategy',
      render: (strategyId) => {
        const strat = strategyMap.get(strategyId);
        if (!strat) return <span className="text-muted-foreground/50">—</span>;
        return (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-foreground truncate max-w-[100px]">{strat.name}</span>
            <Badge variant={strat.trading_mode === 'live' ? 'warning' : 'info'} className="text-[9px] px-1 py-0">
              {strat.trading_mode === 'live' ? 'L' : 'P'}
            </Badge>
          </div>
        );
      },
    },
    { key: 'quantity', label: 'Qty', cellClassName: 'hidden sm:table-cell' },
    { key: 'price', label: 'Price', render: (price) => formatMoney(price) },
    {
      key: 'pnl', label: 'P&L',
      render: (pnl) => {
        const value = parseFloat(pnl) || 0;
        return <span className={pnlClass(value)}>{formatMoney(value)}</span>;
      },
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner className="h-16 w-16 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <PageTransition className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
      {/* ── Top Stats Row ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 auto-rows-fr">
        <Card className="[&>div]:!p-3 [&>div]:sm:!p-4">
          <p className="text-lg font-bold text-foreground mb-2">Strategies</p>
          <div className="flex items-stretch">
            <div className="flex-1 pr-3">
              <p className="text-[10px] font-medium text-yellow-400">Live</p>
              <p className="text-2xl font-bold text-foreground leading-tight">{stats.liveStrategies}</p>
              <p className="text-[10px] text-muted-foreground/50">{stats.liveActive} active</p>
            </div>
            <div className="w-px bg-border/30 self-stretch my-0.5" />
            <div className="flex-1 pl-3">
              <p className="text-[10px] font-medium text-blue-400">Paper</p>
              <p className="text-2xl font-bold text-foreground leading-tight">{stats.paperStrategies}</p>
              <p className="text-[10px] text-muted-foreground/50">{stats.paperActive} active</p>
            </div>
          </div>
        </Card>
        <Card className="[&>div]:!p-3 [&>div]:sm:!p-4">
          <p className="text-lg font-bold text-foreground mb-2">Open Positions</p>
          <div className="flex items-stretch">
            <div className="flex-1 pr-3">
              <p className="text-[10px] font-medium text-yellow-400">Live</p>
              <p className="text-2xl font-bold text-foreground leading-tight">{stats.liveOpenPositions}</p>
              <p className="text-[10px] text-muted-foreground/50">{formatMoney(stats.liveExposure)}</p>
            </div>
            <div className="w-px bg-border/30 self-stretch my-0.5" />
            <div className="flex-1 pl-3">
              <p className="text-[10px] font-medium text-blue-400">Paper</p>
              <p className="text-2xl font-bold text-foreground leading-tight">{stats.paperOpenPositions}</p>
              <p className="text-[10px] text-muted-foreground/50">{formatMoney(stats.paperExposure)}</p>
            </div>
          </div>
        </Card>
        <Card className="[&>div]:!p-3 [&>div]:sm:!p-4">
          <p className="text-lg font-bold text-foreground mb-2">P&L — Live</p>
          <p className={`text-2xl font-bold leading-tight ${pnlClass(stats.livePnL.total)}`}>
            {formatMoney(stats.livePnL.total, { sign: true })}
          </p>
          <p className="text-[10px] text-muted-foreground/50 mt-1">
            <span className={pnlClass(stats.livePnL.unrealized)}>Unreal: {formatMoney(stats.livePnL.unrealized, { sign: true })}</span>
            {' · '}
            <span className={pnlClass(stats.livePnL.realized)}>Real: {formatMoney(stats.livePnL.realized, { sign: true })}</span>
          </p>
        </Card>
        <Card className="[&>div]:!p-3 [&>div]:sm:!p-4">
          <p className="text-lg font-bold text-foreground mb-2">P&L — Paper</p>
          <p className={`text-2xl font-bold leading-tight ${pnlClass(stats.paperPnL.total)}`}>
            {formatMoney(stats.paperPnL.total, { sign: true })}
          </p>
          <p className="text-[10px] text-muted-foreground/50 mt-1">
            <span className={pnlClass(stats.paperPnL.unrealized)}>Unreal: {formatMoney(stats.paperPnL.unrealized, { sign: true })}</span>
            {' · '}
            <span className={pnlClass(stats.paperPnL.realized)}>Real: {formatMoney(stats.paperPnL.realized, { sign: true })}</span>
          </p>
        </Card>
      </div>

      {/* ── Equity Graphs ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <Card title="Live Equity Curve" subtitle="Cumulative realized P&L from trades">
          <div className="h-48">
            <EquityMiniChart trades={allTrades} mode="live" />
          </div>
          <div className="flex items-center justify-between mt-2 px-1">
            <span className="text-[10px] text-muted-foreground/50">
              Aggregate: <span className={pnlClass(stats.totalPnL.total)}>{formatMoney(stats.totalPnL.total, { sign: true })}</span>
            </span>
            <span className="text-[10px] text-muted-foreground/50">
              {tradeCounts.live} trades
            </span>
          </div>
        </Card>

        <Card title="Paper Equity Curve" subtitle="Cumulative realized P&L from paper trades">
          <div className="h-48">
            <EquityMiniChart trades={allTrades} mode="paper" />
          </div>
          <div className="flex items-center justify-between mt-2 px-1">
            <span className="text-[10px] text-muted-foreground/50">
              Aggregate: <span className={pnlClass(stats.paperPnL.total)}>{formatMoney(stats.paperPnL.total, { sign: true })}</span>
            </span>
            <span className="text-[10px] text-muted-foreground/50">
              {tradeCounts.paper} trades
            </span>
          </div>
        </Card>
      </div>

      {/* ── Active Strategies ── */}
      <Card
        title="Active Strategies"
        subtitle={`${stats.activeStrategies} running`}
        action={
          <Link to="/strategies/manager">
            <Button variant="ghost" size="sm">View All →</Button>
          </Link>
        }
      >
        {strategies.filter(s => s.status === 'running').length > 0 ? (
          <Table
            columns={strategyColumns}
            data={strategies.filter(s => s.status === 'running').slice(0, 5)}
          />
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">No active strategies</p>
            <Link to="/strategies/builder">
              <Button>Create a Strategy</Button>
            </Link>
          </div>
        )}
      </Card>

      {/* ── Recent Trades ── */}
      <Card
        title="Recent Trades"
        subtitle="Last 10 trades across all strategies"
        action={
          <Link to="/home/history">
            <Button variant="ghost" size="sm">View All →</Button>
          </Link>
        }
      >
        <Table
          columns={tradeColumns}
          data={recentTrades}
          emptyMessage="No trades executed yet"
        />
      </Card>
    </PageTransition>
  );
}

export default Dashboard;
